import AsyncStorage from '@react-native-async-storage/async-storage';
import { Environment } from '../environments';

export enum FetchMethod {
  Get = 'GET',
  Patch = 'PATCH',
  Post = 'POST',
  Put = 'PUT',
  Delete = 'DELETE'
}

export interface FetchError {
  statusCode: number;
  error: string;
  message: string;
  code?: string;
}

export interface FetchRequest {
  response: Promise<Response>;
  abort: () => void;
}

export interface ApiParams {
  [key: string]: any;
}

const fetchTimeoutMs = 10_000;

export function createNamedError(name: string, message: string): Error {
  const error = new Error(message);
  error.name = name;
  return error;
}

export const api = {
  accessToken: '',
  environment: {} as Environment,
  setEnvironment: (environment: Environment) => {
    api.environment = environment;
  },
  getBaseUrl: (): string => api.environment.apiBaseUrl,
  getAccessToken: async () => {
    // return Cookies.get(api.environment.accessTokenKey);
    const accessToken = await AsyncStorage.getItem(
      api.environment.accessTokenKey
    );
    api.accessToken = accessToken as string;
    return accessToken;
  },
  setAccessToken: (token: string) => {
    // Cookies.set(api.environment.accessTokenKey, token, {
    //   expires: api.environment.expires || 365,
    //   domain: api.environment.domain || window.location.hostname,
    //   secure: api.environment.secure || false
    // });
    api.accessToken = token;
    AsyncStorage.setItem(api.environment.accessTokenKey, token);
  },
  removeAccessToken: () => {
    // Cookies.remove(api.environment.accessTokenKey, {
    //   domain: api.environment.domain || window.location.hostname
    // }),
    api.accessToken = '';
    AsyncStorage.removeItem(api.environment.accessTokenKey);
  },
  get: (path: string, queryParams: ApiParams): FetchRequest => {
    return api.fetch(FetchMethod.Get, path, queryParams);
  },
  delete: (path: string, queryParams: ApiParams): FetchRequest => {
    return api.fetch(FetchMethod.Delete, path, queryParams);
  },
  patch: (path: string, body: ApiParams): FetchRequest => {
    return api.fetch(FetchMethod.Patch, path, body);
  },
  post: (path: string, body: ApiParams): FetchRequest => {
    return api.fetch(FetchMethod.Post, path, body);
  },
  put: (
    path: string,
    body: ApiParams,
    options?: { accessToken?: string }
  ): FetchRequest => {
    return api.fetch(FetchMethod.Put, path, body, options);
  },
  fetch: (
    method: FetchMethod,
    path: string,
    params: ApiParams = {},
    fetchOptions?: { accessToken?: string }
  ): FetchRequest => {
    const controller = new AbortController();
    const { signal } = controller; // allow aborting the fetch call
    const asyncFetch = async () => {
      if (!api.environment.apiBaseUrl) {
        throw new Error(
          '`api.environment` not setup properly. Missing `apiBaseUrl`.'
        );
      }
      const url = new URL(path, api.environment.apiBaseUrl);
      // console.log('url', url);
      const accessToken =
        fetchOptions?.accessToken ?? (await api.getAccessToken());
      const headers = {
        [api.environment.accessTokenKey]: `Bearer ${accessToken}`
      } as any;
      const options: RequestInit = {
        headers,
        method,
        signal
      };
      switch (method) {
        case FetchMethod.Get:
        case FetchMethod.Delete:
          Object.entries(params).forEach(([key, value]) => {
            url.searchParams.set(
              key,
              typeof value === 'object' ? JSON.stringify(value) : value
            );
          });
          break;
        case FetchMethod.Patch:
        case FetchMethod.Post:
        case FetchMethod.Put:
          (options.headers as any)['content-type'] = 'application/json';
          options.body = JSON.stringify(params);
          break;
      }
      return fetch(url.toString(), options);
    };
    const response = (async () => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          controller.abort();
          reject(createNamedError('TimeoutError', 'Request timed out'));
        }, fetchTimeoutMs);
      });
      try {
        return await Promise.race([asyncFetch(), timeoutPromise]);
      } finally {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
      }
    })();
    return {
      response,
      abort: controller.abort.bind(controller)
    };
  }
};

export default api;
